/*
  PDP pricing A/B test — group resolver.
  ------------------------------------------------------------------
  Deliberately SEPARATE from assets/pdp-ab-group.js (the shoes PDP test). Two concurrent
  tests on the same pages must assign shoppers independently — sharing one resolver would
  put the same people in both variant arms and confound the results of both. Own window
  variable, own global.

  Groups: 'control' (what is live today) | 'variant' (the new pricing block).

  Resolution order, first hit wins:
    1. window.pdpPriceAbGroup             — what INTELLIGEMS sets.
    2. <html data-pdp-price-ab-group="…"> — alternative for IG's CSS/JS injection.

  Intelligems is the only source. There is no URL-parameter or sessionStorage override:
  the group cannot be forced from a link, so nothing a shopper can paste or share puts
  them in an arm they were not assigned to. For manual checks use the console:
  `pdpPriceAb.setGroup('variant')`.

  Until one of those answers, the group is UNDECIDED and NEITHER version renders — the
  price area is blank. It is resolved as early as possible: an animation-frame poll
  decides on the first frame after Intelligems sets its variable, and DOMContentLoaded is
  a backstop that shows control if IG never answers, so the price is never blank for long.

  Intelligems box, per group:
      window.pdpPriceAbGroup = 'variant';
      if (window.pdpPriceAb) window.pdpPriceAb.setGroup('variant');
  The second line covers IG loading AFTER the block has painted; the first covers IG
  loading before this file runs. Neither order is guaranteed, so both are needed.
*/
(function () {
  if (window.pdpPriceAb) return;

  var CONTROL = 'control';
  var VARIANT = 'variant';

  // The arm for anyone Intelligems has not assigned: what is already live, so an
  // unassigned shopper sees no change and the new block only reaches assigned traffic.
  var DEFAULT_GROUP = CONTROL;

  // Until the group is KNOWN, neither version is shown — see `stamp()` below. Falling
  // straight back to control would flash the live price row at shoppers Intelligems is
  // about to put in the variant arm, which is the flicker this whole mechanism exists
  // to remove; showing control "just for a moment" is still showing control.
  var PENDING = 'pending';

  var decided = false;

  function normalize(value) {
    var g = String(value == null ? '' : value).trim().toLowerCase();
    return g === VARIANT || g === CONTROL ? g : null;
  }

  // Returns a group only when something DEFINITE says so; null means "not yet known".
  // Every caller treats null as pending rather than as control.
  function resolveDefinite() {
    var ig = normalize(window.pdpPriceAbGroup);
    if (ig) return ig;
    return normalize(document.documentElement.getAttribute('data-pdp-price-ab-group'));
  }

  var current = resolveDefinite();
  if (current) decided = true;
  else current = DEFAULT_GROUP;

  var listeners = [];

  function decide(group) {
    if (decided) return;
    decided = true;
    current = group;
    stamp();
    notify();
  }

  // The single writer of the <html> attribute the CSS keys off. Owned here, not by the
  // feature script, so the reveal does not depend on that script having loaded.
  function stamp() {
    try {
      document.documentElement.setAttribute('data-ppb-group', decided ? current : PENDING);
    } catch (e) {}
  }

  stamp();

  // Reveal as early as possible, two ways:
  //
  // 1. Poll every animation frame. Intelligems usually sets its variable without calling
  //    setGroup, and waiting on the feature script's own tick would add up to ~120ms of
  //    blank price for no reason. This decides on the first frame after IG answers.
  //
  // 2. Backstop at DOMContentLoaded. If IG has not answered by the time the document is
  //    parsed, show control rather than leaving the price area empty. A missing or
  //    blocked Intelligems must never cost the shopper the price.
  //
  // TRADE-OFF, deliberate: if IG answers AFTER DOMContentLoaded, control is briefly shown
  // and then swapped for the variant — a flicker. That is the cost of never being blank.
  // Move the backstop to `window.load` for more IG headroom at the price of a longer wait.
  if (!decided) {
    (function watchForAssignment() {
      if (decided) return;
      var found = resolveDefinite();
      if (found) {
        decide(found);
        return;
      }
      if (window.requestAnimationFrame) window.requestAnimationFrame(watchForAssignment);
    })();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        decide(DEFAULT_GROUP);
      });
    } else {
      decide(DEFAULT_GROUP);
    }
  }

  function notify() {
    for (var i = 0; i < listeners.length; i += 1) {
      // One throwing subscriber must not stop the others being told.
      try {
        listeners[i](current);
      } catch (e) {}
    }
  }

  window.pdpPriceAb = {
    CONTROL: CONTROL,
    VARIANT: VARIANT,

    getGroup: function () {
      return current;
    },

    // False until something definite has said which arm this shopper is in. While false
    // the price area renders nothing at all.
    isDecided: function () {
      return decided;
    },

    // Re-reads every source; called on the feature's own tick so a late Intelligems
    // assignment lands even if setGroup() was never called.
    refresh: function () {
      var next = resolveDefinite();
      if (next && (!decided || next !== current)) {
        current = next;
        decided = true;
        stamp();
        notify();
      }
      return current;
    },

    // Returns false on an unrecognised value instead of throwing, so a typo in the
    // Intelligems box degrades to control rather than breaking the PDP.
    setGroup: function (group) {
      var next = normalize(group);
      if (!next) return false;
      window.pdpPriceAbGroup = next;
      if (!decided || next !== current) {
        current = next;
        decided = true;
        stamp();
        notify();
      }
      return true;
    },

    onChange: function (fn) {
      if (typeof fn !== 'function') return function () {};
      listeners.push(fn);
      return function () {
        var i = listeners.indexOf(fn);
        if (i > -1) listeners.splice(i, 1);
      };
    },
  };
})();
